import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateUserRolesMutation,
} from '../../api/usersApi';
import { DOCTOR_PALETTE, colorFromTag } from '../appointments/doctorColors';
import { applyApiErrorsToForm } from '../../utils/apiError';
import ApiErrorBanner from '../../components/ApiErrorBanner';
import './staff.css';

const ALL_ROLES = [
  { value: 'Admin',          label: 'Admin' },
  { value: 'Doctor',         label: 'Doctor' },
  { value: 'Therapist',      label: 'Therapist' },
  { value: 'Receptionist',   label: 'Receptionist' },
  { value: 'MarketingStaff', label: 'Marketing Staff' },
  { value: 'Accountant',     label: 'Accountant' },
];

export default function StaffFormModal({ existing, defaultRole, onClose }) {
  const isEdit = Boolean(existing);
  const [createUser, { isLoading: creating }] = useCreateUserMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();
  const [updateRoles] = useUpdateUserRolesMutation();

  const defaults = useMemo(() => {
    if (existing) {
      return {
        userName:       existing.userName,
        email:          existing.email,
        fullName:       existing.fullName,
        phoneNumber:    existing.phoneNumber ?? '',
        password:       '',
        specialization: existing.specialization ?? '',
        licenseNumber:  existing.licenseNumber ?? '',
        bio:            existing.bio ?? '',
        isActive:       existing.isActive ?? true,
        roles:          existing.roles ?? [],
        colorTag:       existing.colorTag ?? '',
      };
    }
    return {
      userName: '', email: '', fullName: '', phoneNumber: '', password: '',
      specialization: '', licenseNumber: '', bio: '',
      isActive: true,
      roles: defaultRole ? [defaultRole] : [],
      colorTag: '',
    };
  }, [existing, defaultRole]);

  const { register, handleSubmit, watch, setValue, setError, formState: { errors, isSubmitting } } =
    useForm({ defaultValues: defaults });
  const [serverError, setServerError] = useState(null);

  useEffect(() => { Object.entries(defaults).forEach(([k, v]) => setValue(k, v)); }, [defaults, setValue]);

  const colorTag = watch('colorTag');
  const customHexEnabled = colorTag?.startsWith('#');
  const [customHex, setCustomHex] = useState(customHexEnabled ? colorTag : '#3b82f6');
  const roles = watch('roles') || [];
  const isDoctor = roles.includes('Doctor');

  const toggleRole = (r) => {
    const set = new Set(roles);
    set.has(r) ? set.delete(r) : set.add(r);
    setValue('roles', Array.from(set), { shouldDirty: true });
  };

  const submit = async (data) => {
    setServerError(null);
    if (!data.roles || data.roles.length === 0) {
      setServerError('Pick at least one role.');
      return;
    }

    const payload = {
      fullName: data.fullName.trim(),
      phoneNumber: data.phoneNumber?.trim() || null,
      colorTag: data.colorTag?.trim() || null,
      specialization: data.specialization?.trim() || null,
      licenseNumber: data.licenseNumber?.trim() || null,
      bio: data.bio?.trim() || null,
    };

    try {
      if (isEdit) {
        await updateUser({ id: existing.id, ...payload, isActive: data.isActive }).unwrap();

        const before = new Set(existing.roles ?? []);
        const after  = new Set(data.roles);
        const changed = before.size !== after.size || [...before].some((r) => !after.has(r));
        if (changed) {
          await updateRoles({ id: existing.id, roles: data.roles }).unwrap();
        }
        toast.success('Staff member updated');
      } else {
        await createUser({
          userName: data.userName.trim(),
          email:    data.email.trim(),
          password: data.password,
          ...payload,
          roles: data.roles,
        }).unwrap();
        toast.success('Staff member created');
      }
      onClose();
    } catch (e) {
      const msg = applyApiErrorsToForm(setError, e);
      setServerError(msg);
      toast.error(msg);
    }
  };

  const initials = (watch('fullName') || 'NN').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const previewColor = colorFromTag(colorTag) ?? { dot: '#cbd5e1', bg: '#f1f5f9', border: '#cbd5e1', text: '#1e293b' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit(submit)}>
          <div className="modal-header">
            <h2>{isEdit ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
            <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>

          <div className="modal-body">
            <ApiErrorBanner message={serverError} onDismiss={() => setServerError(null)} />

            {/* Avatar + color preview row */}
            <div className="staff-form-preview">
              <div className="staff-form-avatar" style={{
                background: previewColor.bg,
                color: previewColor.text,
                border: `2px solid ${previewColor.border}`,
              }}>
                {initials}
              </div>
              <div className="staff-form-preview-info">
                <div className="staff-form-preview-name">{watch('fullName') || 'New staff member'}</div>
                <div className="staff-form-preview-role">
                  {roles.length ? roles.map((r) => ALL_ROLES.find((x) => x.value === r)?.label || r).join(' · ') : '— role not set —'}
                </div>
                {isDoctor && watch('specialization') && (
                  <div className="staff-form-preview-spec">{watch('specialization')}</div>
                )}
              </div>
            </div>

            {/* Account info */}
            <h3 className="staff-form-section">Account</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full name *</label>
                <input className="form-control" {...register('fullName', { required: 'Required', maxLength: 128 })} />
                {errors.fullName && <div className="form-hint" style={{ color: 'var(--red-500)' }}>{errors.fullName.message}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-control" {...register('phoneNumber')} placeholder="+91…" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Username {!isEdit && '*'}</label>
                <input className="form-control" disabled={isEdit} {...register('userName', { required: !isEdit && 'Required', pattern: { value: /^[a-zA-Z0-9._-]+$/, message: 'Letters, numbers, . _ - only' } })} />
                {errors.userName && <div className="form-hint" style={{ color: 'var(--red-500)' }}>{errors.userName.message}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Email {!isEdit && '*'}</label>
                <input className="form-control" type="email" disabled={isEdit} {...register('email', { required: !isEdit && 'Required' })} />
                {errors.email && <div className="form-hint" style={{ color: 'var(--red-500)' }}>{errors.email.message}</div>}
              </div>
            </div>
            {!isEdit && (
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input className="form-control" type="password" {...register('password', { required: 'Required', minLength: { value: 8, message: 'At least 8 characters' } })} />
                {errors.password && <div className="form-hint" style={{ color: 'var(--red-500)' }}>{errors.password.message}</div>}
                <div className="form-hint">Must include upper + lower case + a digit.</div>
              </div>
            )}

            {/* Roles */}
            <h3 className="staff-form-section">Roles *</h3>
            <div className="staff-role-grid">
              {ALL_ROLES.map((r) => {
                const checked = roles.includes(r.value);
                return (
                  <button
                    type="button"
                    key={r.value}
                    className={`staff-role-chip ${checked ? 'checked' : ''}`}
                    onClick={() => toggleRole(r.value)}
                  >
                    {checked && <Check size={12} />} {r.label}
                  </button>
                );
              })}
            </div>

            {/* Doctor color picker */}
            {(isDoctor || roles.includes('Therapist')) && (
              <>
                <h3 className="staff-form-section">Calendar color</h3>
                <div className="staff-color-grid">
                  <button
                    type="button"
                    className={`staff-color-swatch ${!colorTag ? 'selected' : ''}`}
                    onClick={() => setValue('colorTag', '', { shouldDirty: true })}
                    title="Auto (hashed from user id)"
                  >
                    <div className="staff-color-swatch-dot" style={{
                      background: 'conic-gradient(from 0deg, #ef4444, #f59e0b, #84cc16, #06b6d4, #a855f7, #ef4444)',
                    }} />
                    <span>Auto</span>
                  </button>
                  {DOCTOR_PALETTE.map((c) => (
                    <button
                      type="button"
                      key={c.name}
                      className={`staff-color-swatch ${colorTag === c.name ? 'selected' : ''}`}
                      onClick={() => setValue('colorTag', c.name, { shouldDirty: true })}
                      title={c.name}
                    >
                      <div className="staff-color-swatch-dot" style={{ background: c.dot }} />
                      <span style={{ textTransform: 'capitalize' }}>{c.name}</span>
                    </button>
                  ))}
                </div>
                <div className="staff-color-custom">
                  <label style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Or a custom hex:</label>
                  <input
                    type="color"
                    value={customHex}
                    onChange={(e) => {
                      setCustomHex(e.target.value);
                      setValue('colorTag', e.target.value, { shouldDirty: true });
                    }}
                    style={{ width: 40, height: 30, padding: 0, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    placeholder="#abc123"
                    value={colorTag?.startsWith('#') ? colorTag : ''}
                    onChange={(e) => setValue('colorTag', e.target.value, { shouldDirty: true })}
                    style={{ maxWidth: 120, fontFamily: 'var(--font-mono)', fontSize: '0.83rem' }}
                    className="form-control"
                  />
                </div>
              </>
            )}

            {/* Doctor-only fields */}
            {isDoctor && (
              <>
                <h3 className="staff-form-section">Doctor details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Specialization</label>
                    <input className="form-control" placeholder="e.g., Sports Physiotherapy" {...register('specialization', { maxLength: 128 })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">License / Registration #</label>
                    <input className="form-control" placeholder="e.g., MCI-12345" {...register('licenseNumber', { maxLength: 64 })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Bio</label>
                  <textarea className="form-control" rows={3} placeholder="Optional short bio shown on patient-facing pages" {...register('bio', { maxLength: 1000 })} />
                </div>
              </>
            )}

            {/* Active toggle (edit only) */}
            {isEdit && (
              <>
                <h3 className="staff-form-section">Status</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem', cursor: 'pointer' }}>
                  <input type="checkbox" {...register('isActive')} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--green-600)' }} />
                  Active (uncheck to disable login for this user)
                </label>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={creating || updating || isSubmitting}>
              {creating || updating ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Staff')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
